import { useState, useCallback } from 'react';
import type React from 'react';

interface UseFormSubmissionOptions {
  onSubmit: () => Promise<void> | void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseFormSubmissionReturn {
  isSubmitting: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  getFieldProps: () => { disabled: boolean };
  getSubmitButtonProps: (
    text: string,
    loadingText?: string,
  ) => {
    type: 'submit';
    disabled: boolean;
    children: string;
  };
  getActionButtonProps: () => { disabled: boolean };
}

/**
 * Custom hook to manage form submission state consistently across all forms.
 *
 * Provides centralized logic for:
 * - Managing loading/submitting state
 * - Disabling all form elements during submission
 * - Consistent submit button text and state
 * - Preventing accidental cancellation during submission
 *
 * @example
 * ```tsx
 * const { isSubmitting, handleSubmit, getFieldProps, getSubmitButtonProps, getActionButtonProps } = useFormSubmission({
 *   onSubmit: async () => {
 *     await userApiClient.post('/api/endpoint', data);
 *   },
 *   onSuccess: () => {
 *     showToast('Success!');
 *     closeModal();
 *   }
 * });
 *
 * return (
 *   <form onSubmit={handleSubmit}>
 *     <input {...getFieldProps()} />
 *     <button type="button" {...getActionButtonProps()}>Cancel</button>
 *     <button {...getSubmitButtonProps('Save', 'Saving...')} />
 *   </form>
 * );
 * ```
 */
export function useFormSubmission({
  onSubmit,
  onSuccess,
  onError,
}: UseFormSubmissionOptions): UseFormSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (isSubmitting) {
        return; // Prevent double submission
      }

      setIsSubmitting(true);
      try {
        await onSubmit();
        onSuccess?.();
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Submission failed'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, onSubmit, onSuccess, onError],
  );

  const getFieldProps = useCallback(
    () => ({
      disabled: isSubmitting,
    }),
    [isSubmitting],
  );

  const getSubmitButtonProps = useCallback(
    (text: string, loadingText?: string) => ({
      type: 'submit' as const,
      disabled: isSubmitting,
      children: isSubmitting ? loadingText || 'Submitting...' : text,
    }),
    [isSubmitting],
  );

  const getActionButtonProps = useCallback(
    () => ({
      disabled: isSubmitting,
    }),
    [isSubmitting],
  );

  return {
    isSubmitting,
    handleSubmit,
    getFieldProps,
    getSubmitButtonProps,
    getActionButtonProps,
  };
}
